import { useIsMapBuilderEditorAllowed } from "@/lib/useIsMapBuilderEditorAllowed";
import { ConfirmModal } from "@cmp-src/overlay/modals/ConfirmModal";
import { Button } from "@gettactic/components/src/buttons/Button/Button";
import { ExpandIcon } from "@gettactic/components/src/icons/TacticIcons/ExpandIcon";
import { useDisclosure } from "@gettactic/helpers/src/hooks";
import { useAuthenticated, useIsDesignSystem2 } from "@gettactic/hooks";
import { CogIcon, XIcon } from "@heroicons/react/outline";
import { useMap } from "@webV2/map/MapProvider";
import { UseMapViewSvgEditReturn } from "@webV2/map/MapView/MapViewSvg/useMapViewSvgEdit";
import React from "react";

export interface MapActionsProps {
  editor?: UseMapViewSvgEditReturn;
}

export const MapActions: React.FC<MapActionsProps> = ({ editor }) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const {
    isEmbedded,
    isMapBuilderMap,
    setForceMapBuilder,
    editingMapDisclosure,
    fullscreenMapDisclosure
  } = useMap();
  const editConfirmDisclosure = useDisclosure();
  const canEditMapBuilder = useIsMapBuilderEditorAllowed();
  const allowEditMode = authenticatedUser?.permissions.settings;

  const isV2 = useIsDesignSystem2();

  return (
    <div className="mr-8 hidden items-center md:flex">
      {editor?.isResourcePanelEditorOpen || editor?.isMapBuilderEditorOpen ? (
        <>
          <ConfirmModal
            size={"xl"}
            title={"Unsaved changes"}
            isOpen={editConfirmDisclosure.isOpen}
            onOpenChange={editConfirmDisclosure.onOpenChange}
            onConfirm={async () => {
              await editor?.mapBuilderRef?.save();
              editingMapDisclosure.close();
              editor?.closeMapBuilderEditor();
              editConfirmDisclosure.close();
            }}
            onExtra={() => {
              editingMapDisclosure.close();
              editor?.closeMapBuilderEditor();
              editConfirmDisclosure.close();
            }}
            cancelLabel={"Stay Here"}
            extraLabel={"Exit without save"}
            confirmLabel={"Save and close"}
          >
            Are you sure you want to exit the map edit mode? All your unsaved
            changes will be lost.
          </ConfirmModal>
          <Button
            size={"sm"}
            onClick={() => {
              if (isMapBuilderMap) {
                editConfirmDisclosure.open();
                return;
              }
              editor?.endEdit();
            }}
            type="button"
            iconRight={XIcon}
            className={"items-center"}
          >
            {isMapBuilderMap ? "Exit Map Builder" : "Exit Map Edit Mode"}
          </Button>
        </>
      ) : (
        <div className={"flex gap-2"}>
          {allowEditMode && !isEmbedded && !fullscreenMapDisclosure.isOpen ? (
            <Button
              size={"sm"}
              onClick={async () => {
                if (isMapBuilderMap) {
                  editor?.openMapBuilderEditor();
                  return;
                }
                editor?.openResourceEditPanel();
              }}
              type="button"
              iconRight={CogIcon}
              className={"items-center"}
            >
              {isMapBuilderMap ? "Open Map Builder" : "Map Edit Mode"}
            </Button>
          ) : null}
          {!isMapBuilderMap &&
          canEditMapBuilder &&
          allowEditMode &&
          !isEmbedded &&
          !fullscreenMapDisclosure.isOpen ? (
            <Button
              size={"sm"}
              onClick={() => setForceMapBuilder(true)}
              type="button"
              iconRight={CogIcon}
              className={"items-center"}
            >
              Force Map Builder
            </Button>
          ) : undefined}
          {!isMapBuilderMap &&
            !isEmbedded && isV2 && (
              <Button
                size={"sm"}
                onClick={() => fullscreenMapDisclosure.toggle()}
                type="button"
                className={"items-center"}
              >
                <ExpandIcon />
              </Button>
            )}
        </div>
      )}
    </div>
  );
};
